import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { usePlausible } from "@/hooks/usePlausible";

const REGULATIONS = ["AFIR", "Eichrecht", "LSV", "BDEW", "EnWG", "CRA"];

const DELIVERABLES = [
  {
    title: "Regulatorische Luecken-Analyse",
    body: "Wir pruefen Ihre Ladeinfrastruktur gegen alle relevanten Vorschriften — AFIR, Eichrecht, LSV, BDEW, EnWG und CRA.",
  },
  {
    title: "Priorisierter Handlungsplan",
    body: "Konkrete Schritte in Reihenfolge nach Risiko und Bussgeld-Hoehe — keine generischen Checklisten, sondern auf Ihren Standort zugeschnitten.",
  },
  {
    title: "Bussgeld- und Risikoschaetzung",
    body: "Quantifizierte Exposition je Verstoss, damit Sie Investitionsentscheidungen mit klaren Zahlen treffen koennen.",
  },
  {
    title: "30-Minuten-Gespraech",
    body: "Persoenliche Durchsprache der Ergebnisse mit ChargeSuite — keine Verkaufspraesentation, sondern Fachaustausch.",
  },
];

export function Audit() {
  const plausible = usePlausible();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || status === "submitting") return;
    setStatus("submitting");
    setErrorMessage("");
    try {
      const res = await fetch("/api/audit-request", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: trimmed, source: "audit-page" }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        setErrorMessage(data.error || "Anfrage konnte nicht gesendet werden.");
        setStatus("error");
        return;
      }
      plausible("audit_form_submit", { props: { source: "audit-page" } });
      setStatus("success");
      setEmail("");
    } catch {
      setErrorMessage("Netzwerkfehler. Bitte erneut versuchen.");
      setStatus("error");
    }
  }

  return (
    <div className="min-h-screen bg-[var(--color-background)] text-[var(--color-text-primary)]">
      {/* ── Header ── */}
      <header className="border-b border-[var(--color-border)] bg-white px-6 py-4">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <Link to="/" className="text-xl font-bold" style={{ letterSpacing: "-0.02em" }}>
            <span style={{ color: "var(--color-navy)" }}>Charge</span>
            <span style={{ color: "var(--color-primary)" }}>Suite</span>
          </Link>
          <nav className="flex items-center gap-6 text-sm text-[var(--color-text-secondary)]">
            <Link to="/" className="transition-colors hover:text-[var(--color-text-primary)]">
              Zur Startseite
            </Link>
          </nav>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="px-6 py-20 sm:py-28">
        <div className="mx-auto max-w-3xl text-center">
          <span
            className="inline-block rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider"
            style={{ background: "var(--color-primary-soft)", color: "var(--color-primary)" }}
          >
            Kostenlos &middot; Unverbindlich
          </span>
          <h1 className="mt-6 text-4xl font-bold tracking-tight sm:text-5xl" style={{ color: "var(--color-navy)" }}>
            Compliance-Audit fuer Ihre Ladeinfrastruktur
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-[var(--color-text-secondary)]">
            In 30 Minuten zeigen wir Ihnen, wo Ihre Ladeinfrastruktur regulatorisch steht — mit
            KI-gestuetzter Analyse gegen die sieben relevanten deutschen und europaeischen Vorschriften.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2 text-xs text-[var(--color-text-muted)]">
            {REGULATIONS.map((reg) => (
              <span
                key={reg}
                className="rounded-full border border-[var(--color-border)] bg-white px-3 py-1 font-medium"
              >
                {reg}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── Deliverables ── */}
      <section className="bg-white px-6 py-16">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-center text-2xl font-bold sm:text-3xl" style={{ color: "var(--color-navy)" }}>
            Das bekommen Sie
          </h2>
          <div className="mt-12 grid gap-8 sm:grid-cols-2">
            {DELIVERABLES.map((item, index) => (
              <div key={item.title} className="flex gap-4">
                <div
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-bold text-white"
                  style={{ background: "var(--color-primary)" }}
                >
                  {index + 1}
                </div>
                <div>
                  <h3 className="text-lg font-semibold" style={{ color: "var(--color-navy)" }}>
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-[var(--color-text-secondary)]">
                    {item.body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="bg-[var(--color-primary)] px-6 py-20 sm:py-28">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Jetzt Audit anfordern
          </h2>
          <p className="mt-5 text-base leading-relaxed text-blue-100">
            Hinterlassen Sie Ihre E-Mail — wir melden uns binnen eines Werktages mit einem Terminvorschlag.
          </p>
          <form
            onSubmit={handleSubmit}
            className="mt-10 flex flex-col items-center gap-3 sm:flex-row sm:justify-center"
          >
            <label htmlFor="audit-email" className="sr-only">
              E-Mail-Adresse
            </label>
            <input
              id="audit-email"
              type="email"
              required
              disabled={status === "submitting" || status === "success"}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ihre@email.de"
              className="w-full rounded-xl border border-blue-300 bg-white px-5 py-3.5 text-sm text-[var(--color-text-primary)] placeholder-[var(--color-text-muted)] shadow-sm focus:outline-none focus:ring-2 focus:ring-white disabled:opacity-60 sm:w-80"
            />
            <button
              type="submit"
              disabled={status === "submitting" || status === "success"}
              className="w-full rounded-xl bg-white px-7 py-3.5 text-sm font-semibold text-[var(--color-primary)] shadow-sm transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              {status === "submitting"
                ? "Wird gesendet..."
                : status === "success"
                  ? "Anfrage gesendet!"
                  : "Audit buchen"}
            </button>
          </form>
          {status === "success" && (
            <p className="mt-4 text-sm font-medium text-white" role="status">
              Vielen Dank — Sie erhalten binnen weniger Minuten eine Bestaetigung per E-Mail.
            </p>
          )}
          {status === "error" && (
            <p className="mt-4 text-sm font-medium text-amber-200" role="alert">
              {errorMessage}
            </p>
          )}
          <p className="mt-5 text-sm text-blue-200">
            Oder direkt per E-Mail:{" "}
            <a
              href="mailto:contact@chargesuite.de"
              className="font-medium text-white underline decoration-blue-300 hover:decoration-white"
            >
              contact@chargesuite.de
            </a>
          </p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-[var(--color-border)] bg-white px-6 py-10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 sm:flex-row">
          <span className="text-base font-bold" style={{ letterSpacing: "-0.02em" }}>
            <span style={{ color: "var(--color-navy)" }}>Charge</span>
            <span style={{ color: "var(--color-primary)" }}>Suite</span>
          </span>
          <nav className="flex flex-wrap items-center justify-center gap-6 text-sm text-[var(--color-text-secondary)]">
            <Link to="/impressum" className="transition-colors hover:text-[var(--color-text-primary)]">
              Impressum
            </Link>
            <Link to="/privacy" className="transition-colors hover:text-[var(--color-text-primary)]">
              Datenschutz
            </Link>
            <Link to="/terms" className="transition-colors hover:text-[var(--color-text-primary)]">
              AGB
            </Link>
          </nav>
          <p className="text-sm text-[var(--color-text-muted)]">ChargeSuite 2026. Made in Germany.</p>
        </div>
      </footer>
    </div>
  );
}
